import type { Currency, WorkspaceIDs } from "../../entities/ledger/types";
import { apiRequest } from "../../shared/api/client";
import type { MovementForm } from "./model";

export type MovementKind = "expense" | "income" | "transfer";

export interface TransactionCommandEnvelope {
  kind: "expense" | "income" | "transfer" | "exchange" | "refund" | "reversal" | "journal";
  payload: ExpensePayload | IncomePayload | TransferPayload | ExchangePayload | RefundPayload | ReversalPayload | ManualJournalPayload;
}
export interface ExpensePayload { meta: object; assetAccountId: string; amount: object; paid: object; expenseAccountId?: string; fees?: object[] }
export interface IncomePayload { meta: object; assetAccountId: string; amount: object; received: object; incomeAccountId?: string; fees?: object[] }
export interface TransferPayload { meta: object; sourceAccountId: string; destinationAccountId: string; amount: object; fees?: object[] }
export interface ExchangePayload { meta: object; sourceAccountId: string; destinationAccountId: string; sold: object; received: object; fees?: object[] }
export interface RefundPayload { meta: object; originalId: string; assetAccountId: string; amount: object; received?: object }
export interface ReversalPayload { meta: object; originalId: string }
export interface ManualJournalPayload { meta: object; lines: object[]; references?: object[] }

const scales: Record<Currency, number> = { GEL: 2, USD: 2, EUR: 2, GBP: 2, TRY: 2, RUB: 2, JPY: 0, KWD: 3, UAH: 2, VND: 0, PLN: 2, CHF: 2 };

function minor(amount: string, currency: Currency): number {
  const scale = scales[currency];
  const [whole, fraction = ""] = amount.split(".");
  if (fraction.length > scale) throw new Error(`${currency} supports ${scale} decimal places.`);
  const value = Number(`${whole}${fraction.padEnd(scale, "0")}`);
  if (!Number.isSafeInteger(value)) throw new Error("The amount is too large.");
  return value;
}

function decimal(value: number, currency: Currency): string {
  const scale = scales[currency];
  const sign = value < 0 ? "-" : "";
  const absolute = Math.abs(value).toString().padStart(scale + 1, "0");
  if (scale === 0) return `${sign}${absolute}`;
  return `${sign}${absolute.slice(0, -scale)}.${absolute.slice(-scale)}`;
}

export function recordMovement(
  kind: MovementKind,
  values: MovementForm,
  ids: WorkspaceIDs,
  currency: Currency,
): Promise<unknown> {
  const meta = { effectiveAt: values.effectiveAt, description: values.description, ...(values.categoryId ? { categoryId: values.categoryId } : {}) };
  const feeMinor = values.fee === "" ? 0 : minor(values.fee, currency);
  const fees = feeMinor > 0 ? [{
    chargedAccountId: kind === "income" ? undefined : values.accountId,
    // The server resolves the book's fee account for this currency.
    expenseAccountId: undefined,
    amount: { amount: values.fee, currency },
  }] : [];
  if (kind === "income") {
    const receivedMinor = minor(values.amount, currency) - feeMinor;
    if (receivedMinor < 0) throw new Error("The fee cannot exceed gross income.");
    return apiRequest(`/v1/books/${encodeURIComponent(ids.book)}/transactions`, {
      method: "POST",
      idempotent: true,
      body: { kind: "income", payload: {
        meta,
        assetAccountId: values.accountId,
        incomeAccountId: undefined,
        amount: { amount: values.amount, currency },
        received: { amount: decimal(receivedMinor, currency), currency },
        fees,
      } },
    });
  }
  if (kind === "transfer") {
    if (!values.destinationAccountId || values.destinationAccountId === values.accountId) {
      throw new Error("Choose two different accounts for a transfer.");
    }
    return apiRequest(`/v1/books/${encodeURIComponent(ids.book)}/transactions`, {
      method: "POST",
      idempotent: true,
      body: { kind: "transfer", payload: {
        meta,
        sourceAccountId: values.accountId,
        destinationAccountId: values.destinationAccountId,
        amount: { amount: values.amount, currency },
        fees,
      } },
    });
  }
  return apiRequest(`/v1/books/${encodeURIComponent(ids.book)}/transactions`, {
    method: "POST",
    idempotent: true,
    body: { kind: "expense", payload: {
      meta,
      assetAccountId: values.accountId,
      expenseAccountId: undefined,
      amount: { amount: values.amount, currency },
      paid: { amount: values.amount, currency },
      fees,
    } },
  });
}

export function reverseTransaction(bookID: string, transactionID: string): Promise<unknown> {
  return apiRequest(`/v1/books/${encodeURIComponent(bookID)}/transactions`, {
      method: "POST", idempotent: true,
      body: { kind: "reversal", payload: {
        originalId: transactionID,
        meta: { effectiveAt: new Date().toISOString(), description: "Correction" },
      } },
    },
  );
}

// A refund returns part or all of an expense and stays tied to it: spending
// nets down instead of the money arriving as income. The server takes the
// expense account from the original when it is not named.
export function refundTransaction(
  bookID: string,
  originalID: string,
  input: { assetAccountId: string; amount: string; currency: Currency; effectiveAt: string; description: string },
): Promise<unknown> {
  return apiRequest(`/v1/books/${encodeURIComponent(bookID)}/transactions`, {
    method: "POST",
    idempotent: true,
    body: { kind: "refund", payload: {
      meta: { effectiveAt: input.effectiveAt, description: input.description || "Refund" },
      originalId: originalID,
      assetAccountId: input.assetAccountId,
      amount: { amount: input.amount, currency: input.currency },
    } },
  });
}
